// src/pages/ViewTeam.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, getDocs,collection } from "firebase/firestore";
import { db } from "../lib/firebase";
import Pitch from "../central/Pitch";
import Button from "../central/Button";
import { HeadingLarge as Heading, HeadingSmall as Subheading, BodyText } from "../central/Typography";
import { useAuth } from "../context/AuthContext";
import { paths } from "../lib/paths";
import { getLeagueConfig } from "../utils/leagueConfig";
import { isUnlimitedFT } from "../utils/freeTransfers";
import { logError } from "../utils/logError";
import Panel from "../central/Panel";
import useMobileView from "../central/useMobileview";
import { List, LayoutGrid } from "lucide-react";
import "../central/PitchPages.css";
import PageLoader from "../components/PageLoader";
import { useSeason } from "../context/SeasonContext";

export default function ViewTeam() {
  const { leagueID } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedSeasonID } = useSeason();

  const [loading, setLoading] = useState(true);
  const [currentGW, setCurrentGW] = useState(null);
  const [selectedGW, setSelectedGW] = useState(null);
  const [selection, setSelection] = useState({});
  const [captain, setCaptain] = useState("");
  const [chip, setChip] = useState("None");
  const [playersById, setPlayersById] = useState({});
  const [teamName, setTeamName] = useState("");
  const [positions, setPositions] = useState([]);
  const [availableGWs, setAvailableGWs] = useState([]);
  // Added state for GW snapshot metadata and finance
  const [metaForGW, setMetaForGW] = useState({});
  const [budgetForGW, setBudgetForGW] = useState(null);
  const [transfersUsedForGW, setTransfersUsedForGW] = useState(null);
  const [transfersFreeForGW, setTransfersFreeForGW] = useState(null);
  const [teamData, setTeamData] = useState({});

  // Mobile view toggle state
  const [activeView, setActiveView] = useState("pitch");
  const { isMobileView } = useMobileView();

  useEffect(() => {
    if (!currentUser || !leagueID) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // 1) Determine teamID from user doc
        const userRef = doc(paths.users(), currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          setLoading(false);
          return;
        }
        const teamID = userSnap.data()?.teams?.[leagueID];
        if (!teamID) {
          setLoading(false);
          return;
        }

        // 2) Load league doc to get currentGW
        const leagueRef = paths.league(leagueID);
        const leagueSnap = await getDoc(leagueRef);
        const gw = leagueSnap.exists() ? leagueSnap.data()?.currentGW || "GW1" : "GW1";
        setCurrentGW(gw);
        setSelectedGW(gw);

        // 3) Load team doc (under league/teams)
        const teamRef = doc(paths.leagueTeams(leagueID, selectedSeasonID), teamID);
        const teamSnap = await getDoc(teamRef);

        let t = null;
        if (teamSnap.exists()) {
          t = teamSnap.data();
          setTeamData(t);
          const type = t.type || "7-a-side";
          const config = getLeagueConfig(type) || { positions: ["DEF", "MID", "FWD"] };
          setPositions(config.positions || ["DEF", "MID", "FWD"]);

          setTeamName(t?.name || t?.teamName || "");

          // --- Build available gameweeks up to currentGW (GW1..current) ---
          const gwNumber = parseInt(gw.replace("GW", ""), 10) || 1;
          const combinedGWs = Array.from({ length: gwNumber }, (_, i) => `GW${i + 1}`);

          setAvailableGWs(combinedGWs);

          // Pick selected GW: prioritize currentGW if available; otherwise use latest
          const newSelectedGW = combinedGWs.includes(gw)
            ? gw
            : combinedGWs[combinedGWs.length - 1];
          setSelectedGW(newSelectedGW);

          const emptySelection = (Array.isArray(config?.positions) ? config.positions : ["DEF", "MID", "FWD"]).reduce((acc, pos) => {
            acc[pos] = [];
            return acc;
          }, {});

          // Attempt to read from gameweeks subcollection for currentGW
          const gwDocRef = doc(paths.gwTeams(leagueID, newSelectedGW, selectedSeasonID), teamID);
          const gwDocSnap = await getDoc(gwDocRef);

          let newSelection;
          let newCaptain = "";
          let newChip = "None";

          if (gwDocSnap.exists()) {
            const gwData = gwDocSnap.data();
            newSelection =
              (gwData.selections && gwData.selections[selectedGW]) ||
              (t.selections && t.selections[newSelectedGW]) ||
              t[newSelectedGW] ||
              emptySelection;

            newCaptain =
              (gwData.captains && gwData.captains[newSelectedGW]) ||
              (t.captains && t.captains[newSelectedGW]) ||
              "";

            newChip =
              gwData.chip ||
              (t.chipsUsed && t.chipsUsed[newSelectedGW]) ||
              "None";
          } else {
            newSelection =
              (t.selections && t.selections[newSelectedGW]) ||
              t[newSelectedGW] ||
              emptySelection;

            newCaptain = (t.captains && t.captains[newSelectedGW]) || "";
            newChip = (t.chipsUsed && t.chipsUsed[newSelectedGW]) || "None";
          }

          setSelection(newSelection);
          setCaptain(newCaptain);
          setChip(newChip);

          // --- Set meta and finance state from snapshot (newSelectedGW) ---
          const selGW = newSelectedGW;
          const meta = gwDocSnap.exists()
            ? gwDocSnap.data()?.squadMeta?.[selGW] || {}
            : (await getDoc(doc(paths.gwTeams(leagueID, selGW, selectedSeasonID), teamID))).data()?.squadMeta?.[selGW] || {};
          setMetaForGW(meta);

          const currBudget = gwDocSnap.exists()
            ? gwDocSnap.data()?.currentBudget?.[selGW]
            : t?.currentBudget?.[selGW];
          setBudgetForGW(typeof currBudget === 'number' ? currBudget : null);

          const made = t?.transfersMade?.[selGW];
          const free = t?.transfersFree?.[selGW];
          setTransfersUsedForGW(typeof made === 'number' ? made : null);
          setTransfersFreeForGW(
            typeof free === 'number' || isUnlimitedFT(free) ? free : null
          );
          // --- end snapshot meta/finance load ---

          console.log("availableGWs:", combinedGWs);
          console.log("selectedGW:", newSelectedGW);
          console.log("selection:", selection);
        } else {
          // If no team doc, fallback positions as well
          const config = getLeagueConfig("7-a-side") || { positions: ["DEF", "MID", "FWD"] };
          setPositions(config.positions || ["DEF", "MID", "FWD"]);
          const gwNumber = parseInt(gw.replace("GW", ""), 10) || 1;
          const allGWs = Array.from({ length: gwNumber }, (_, i) => `GW${i + 1}`);
          setAvailableGWs(allGWs);
          const newSelectedGW = allGWs.includes(gw)
            ? gw
            : allGWs[allGWs.length - 1];
          setSelectedGW(newSelectedGW);
          setSelection(
            (Array.isArray(config?.positions) ? config.positions : ["DEF", "MID", "FWD"]).reduce((acc, pos) => {
              acc[pos] = [];
              return acc;
            }, {})
          );
          // Also clear meta/finance state
          setMetaForGW({});
          setBudgetForGW(null);
          setTransfersUsedForGW(null);
          setTransfersFreeForGW(null);

          console.log("availableGWs:", allGWs);
          console.log("selectedGW:", newSelectedGW);
          console.log("selection:", selection);
        }

        // 4) Load players in this league
        // Keeping this same as PickTeam: load all players in leaguePlayers collection
        const playersCollectionRef = paths.leaguePlayers(leagueID, selectedSeasonID);
        const playersSnap = await getDocs(playersCollectionRef);
        if (cancelled) return;
        const map = {};
        playersSnap.forEach((d) => (map[d.id] = d.data()));
        setPlayersById(map);
      } catch (err) {
        if (cancelled) return;
        console.error("ViewTeam load failed:", err);
        await logError({
          leagueID,
          userID: currentUser?.uid || "unknown",
          action: "ViewTeam_initialLoad",
          payload: { leagueID, currentUser: !!currentUser },
          error: err
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // NOTE: selectedSeasonID is used inside (team/players paths) and must be a
    // dependency, otherwise toggling the season pill never refetches this page.
  }, [currentUser, leagueID, selectedSeasonID]);

  useEffect(() => {
    if (!selectedGW) return;
    let cancelled = false;
    (async () => {
      try {
        // Reload team doc to update selection, captain, chip for selectedGW
        if (!currentUser || !leagueID) return;
        const userRef = doc(paths.users(), currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) return;
        const teamID = userSnap.data()?.teams?.[leagueID];
        if (!teamID) return;
        const teamRef = doc(paths.leagueTeams(leagueID, selectedSeasonID), teamID);
        const teamSnap = await getDoc(teamRef);
        if (!teamSnap.exists()) return;
        const t = teamSnap.data();
        const type = t.type || "7-a-side";
        const config = getLeagueConfig(type) || { positions: ["DEF", "MID", "FWD"]};

        const emptySelection = (Array.isArray(config?.positions) ? config.positions : ["DEF", "MID", "FWD"]).reduce((acc, pos) => {
          acc[pos] = [];
          return acc;
        }, {});

        // Attempt to read from gameweeks subcollection for selectedGW
        const gwDocRef = doc(paths.gwTeams(leagueID, selectedGW, selectedSeasonID), teamID);
        const gwDocSnap = await getDoc(gwDocRef);

        let newSelection;
        let newCaptain = "";
        let newChip = "None";

        if (gwDocSnap.exists()) {
          const gwData = gwDocSnap.data();
          newSelection =
            (gwData.selections && gwData.selections[selectedGW]) ||
            (t.selections && t.selections[selectedGW]) ||
            t[selectedGW] ||
            emptySelection;

          newCaptain =
            (gwData.captains && gwData.captains[selectedGW]) ||
            (t.captains && t.captains[selectedGW]) ||
            "";

          newChip =
            gwData.chip ||
            (t.chipsUsed && t.chipsUsed[selectedGW]) ||
            "None";
        } else {
          newSelection =
            (t.selections && t.selections[selectedGW]) ||
            t[selectedGW] ||
            emptySelection;

          newCaptain = (t.captains && t.captains[selectedGW]) || "";
          newChip = (t.chipsUsed && t.chipsUsed[selectedGW]) || "None";
        }

        setSelection(newSelection);
        setCaptain(newCaptain);
        setChip(newChip);

        // --- Set meta and finance state from snapshot (selectedGW) ---
        const meta = gwDocSnap.exists()
          ? gwDocSnap.data()?.squadMeta?.[selectedGW] || {}
          : (await getDoc(doc(paths.gwTeams(leagueID, selectedGW, selectedSeasonID), teamID))).data()?.squadMeta?.[selectedGW] || {};
        setMetaForGW(meta);

        const currBudget = gwDocSnap.exists()
          ? gwDocSnap.data()?.currentBudget?.[selectedGW]
          : t?.currentBudget?.[selectedGW];
        setBudgetForGW(typeof currBudget === 'number' ? currBudget : null);

        const made = t?.transfersMade?.[selectedGW];
        const free = t?.transfersFree?.[selectedGW];
        setTransfersUsedForGW(typeof made === 'number' ? made : null);
        setTransfersFreeForGW(typeof free === 'number' ? free : null);
        // --- end snapshot meta/finance load ---

        console.log("updated selection for selectedGW:", selectedGW, selection);
      } catch (err) {
        if (cancelled) return;
        console.error("ViewTeam update for selectedGW failed:", err);
        await logError({
          leagueID,
          userID: currentUser?.uid || "unknown",
          action: "ViewTeam_updateSelectedGW",
          payload: { selectedGW },
          error: err
        });
      }
    })();
    return () => { cancelled = true; };
    // NOTE: selectedSeasonID is used inside (team/gwTeams paths) and must be a
    // dependency, otherwise toggling the season pill leaves this stale.
  }, [selectedGW, currentUser, leagueID, selectedSeasonID]);

  const hasTeamDoc = Object.keys(teamData || {}).length > 0;

  if (loading) return <PageLoader />;

  if (!hasTeamDoc) {
    return (
      <>
        <h2>No Team Found</h2>
        <p>You haven’t picked a team yet.</p>
      </>
    );
  }

  // Remove any FLEX positions, only DEF, MID, FWD
  const allowedPositions = ["DEF", "MID", "FWD"];
  const filteredPositions = (positions || allowedPositions).filter((pos) => allowedPositions.includes(pos));
  const pickedIds = filteredPositions.flatMap((pos) => selection[pos] || []);
  const hasSelectionThisGW = pickedIds.length > 0;

  const nameOf = (id) => playersById[id]?.name || id;
  const priceOf = (id) => playersById[id]?.currentPrice ?? "-";
  const buyPriceOf = (id) => playersById[id]?.buyPrice ?? "-";
  const posOf = (id) => playersById[id]?.position || "?";

  // Budget and value calculations
  const teamValue = pickedIds.reduce((sum, id) => {
    const price = playersById[id]?.currentPrice;
    return sum + (typeof price === "number" ? price : 0);
  }, 0);
  // Use budgetForGW if available, else fallback to TOTAL_BUDGET - teamValue
  const budgetRemaining = 
  typeof budgetForGW === 'number' ? budgetForGW : "-";

  // Helper: Find buy price from meta (supports both map and array formats)
  function findBuy(meta, pos, id) {
    const block = meta?.[pos];
    if (!block) return undefined;
    if (Array.isArray(block)) {
      const entry = block.find(e => e.id === id);
      return entry?.buyPrice;
    }
    return block?.[id]?.buyPrice;
  }

  // Helper: Get latest buyPrice for a player/pos from metaForGW or previous GWs
  function getLatestBuyPrice(id, pos) {
    const curr = findBuy(metaForGW, pos, id);
    if (typeof curr === "number") return curr;
    if (!availableGWs || !selectedGW) return "-";
    const selIndex = availableGWs.indexOf(selectedGW);
    for (let i = selIndex - 1; i >= 0; i--) {
      const gw = availableGWs[i];
      const metaGW = teamData?.squadMeta?.[gw] || {};
      const bp = findBuy(metaGW, pos, id);
      if (typeof bp === "number") return bp;
    }
    return "-";
  }

  // Group players by position for sidebar, using metaForGW for buyPrice (with fallback)
  const playersByPos = {};
  filteredPositions.forEach((pos) => {
    playersByPos[pos] = (selection[pos] || []).map((id) => {
      const gwPrice = typeof playersById[id]?.priceHistory?.[selectedGW] === "number"
        ? playersById[id].priceHistory[selectedGW]
        : playersById[id]?.currentPrice;

      return {
        id,
        name: nameOf(id),
        buyPrice: getLatestBuyPrice(id, pos),
        currentPrice: gwPrice,
        displayValue: typeof gwPrice === "number" ? `$${gwPrice.toFixed(1)}` : "-",
      };
    });
  });

  const totalBuyValue = Object.values(playersByPos)
    .flat()
    .reduce((sum, p) => (typeof p.buyPrice === "number" ? sum + p.buyPrice : sum), 0);

  const netGainLoss = teamValue - totalBuyValue;

  // ---- Enhance playersById with GW-based displayValue for Pitch ----
  const enhancedPlayersById = {};
  Object.keys(playersById).forEach(id => {
    // default to currentPrice
    let gwPrice = playersById[id]?.currentPrice;

    // if this player appears in playersByPos, use that GW price
    filteredPositions.forEach(pos => {
      (playersByPos[pos] || []).forEach(p => {
        if (p.id === id && typeof p.currentPrice === "number") {
          gwPrice = p.currentPrice;
        }
      });
    });

    enhancedPlayersById[id] = {
      ...playersById[id],
      currentPrice: gwPrice,
      displayValue: typeof gwPrice === "number" ? `$${gwPrice.toFixed(1)}` : "-"
    };
  });

  // Compute transfers using updated logic (prevents GW1 flash, corrects handling)
  let transfersUsed = '-';
  let transfersAvailable = '-';

  const startGW = teamData?.startGW || "GW1";
  const transfersMade = teamData?.transfersMade || {};
  const transfersFree = teamData?.transfersFree || {};

  if (selectedGW && !loading && Object.keys(teamData).length > 0) {
    if (selectedGW === startGW) {
      transfersUsed = 'unlimited';
      transfersAvailable = 'unlimited';
    } else if (
      isUnlimitedFT(transfersFree?.[selectedGW]) ||
      isUnlimitedFT(transfersFreeForGW)
    ) {
      // FT explicitly set to unlimited for this GW (string "unlimited" or a
      // leaked sentinel number like 999/1000) — don't count down.
      transfersUsed = transfersMade?.[selectedGW] ?? transfersUsedForGW ?? 'unlimited';
      transfersAvailable = 'unlimited';
    } else if (typeof transfersFreeForGW === 'number' && typeof transfersUsedForGW === 'number') {
      const used = transfersMade?.[selectedGW] ?? transfersUsedForGW ?? 0;
      const free = transfersFree?.[selectedGW] ?? transfersFreeForGW ?? 0;
      transfersUsed = used;
      transfersAvailable = Math.max(0, free - used);
    }
  }


  // Arrow pill GW selector (matches YourPoints style)
  function GWSelector() {
    const gwList = availableGWs;
    const currentIndex = gwList.indexOf(selectedGW);
    const canPrev = currentIndex > 0;
    const canNext = currentIndex >= 0 && currentIndex < gwList.length - 1;
    const arrowBtn = (enabled) => ({
      background: "none", border: "none", cursor: enabled ? "pointer" : "default",
      padding: "0.3rem 0.6rem", fontSize: "1rem",
      color: enabled ? "#50B4C8" : "#ccc", fontWeight: "700", lineHeight: 1,
    });
    return gwList.length > 0 ? (
      <div style={{ display: "inline-flex", alignItems: "center", background: "#f4f7f8", borderRadius: "999px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <button style={arrowBtn(canPrev)} disabled={!canPrev}
          onClick={() => canPrev && setSelectedGW(gwList[currentIndex - 1])}
          aria-label="Previous gameweek">←</button>
        <span style={{ fontWeight: 600, fontSize: "0.85rem", minWidth: 44, textAlign: "center", color: "#222", padding: "0 0.1rem" }}>{selectedGW || "—"}</span>
        <button style={arrowBtn(canNext)} disabled={!canNext}
          onClick={() => canNext && setSelectedGW(gwList[currentIndex + 1])}
          aria-label="Next gameweek">→</button>
      </div>
    ) : null;
  }

  // Mobile layout
  if (isMobileView) {
    // Construct gwPrices object for Pitch
    const gwPrices = {};
    filteredPositions.forEach(pos => {
      (playersByPos[pos] || []).forEach(p => {
        gwPrices[p.id] = p.currentPrice;
      });
    });
    return (
      <>
        <div style={{ width: "100%", maxWidth: 600, margin: "0 auto", padding: "0 rem 0.1rem" }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "0.5rem", marginBottom: "8px", position: "relative" }}>
            <GWSelector />
            <div style={{ position: "absolute", right: "0.5rem" }}>
              {activeView === "list" ? (
                <button
                  onClick={() => setActiveView("pitch")}
                  style={{ background: "#fff", border: "none", borderRadius: "8px", padding: "6px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", cursor: "pointer", color: "#50B4C8", display: "flex", alignItems: "center" }}
                  aria-label="Switch to pitch view"
                >
                  <LayoutGrid size={18} />
                </button>
              ) : (
                <button
                  onClick={() => setActiveView("list")}
                  style={{ background: "#fff", border: "none", borderRadius: "8px", padding: "6px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", cursor: "pointer", color: "#50B4C8", display: "flex", alignItems: "center" }}
                  aria-label="Switch to list view"
                >
                  <List size={18} />
                </button>
              )}
            </div>
          </div>
          {activeView === "list" && (
            <div style={{ marginTop: "0.2rem" }}>
              <Panel
                style={{
                  width: "100%",
                  margin: "0 auto 0.2rem auto",
                  borderRadius: "8px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                  padding: "1.5rem 1.1rem",
                  maxWidth: 400
                }}
              >
                {!hasSelectionThisGW && (
                  <BodyText
                    variant="muted"
                    style={{ marginBottom: 10, fontSize: "0.9rem" }}
                  >
                    No selections saved for {selectedGW}.
                  </BodyText>
                )}
                {/* Player table grouped by position */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 60px", columnGap: 8, alignItems: "center" }}>
                  {/* Header row */}
                  <BodyText style={{ fontWeight: 600 }}>
                    {(teamName || "Team")} • {(selectedGW || "—")}
                  </BodyText>
                  <BodyText style={{ textAlign: "right", fontWeight: 600 }}>Buy</BodyText>
                  <BodyText style={{ textAlign: "right", fontWeight: 600 }}>{selectedGW}</BodyText>
                  {/* Position groups */}
                  {filteredPositions.map((pos) => {
                    const rows = playersByPos[pos] || [];
                    return (
                      <React.Fragment key={pos}>
                        <Subheading
                          variant="muted"
                          style={{
                            gridColumn: "1 / -1",
                            fontSize: "1rem",
                            fontWeight: 600,
                            margin: "10px 0 4px 0",
                            letterSpacing: 0.2,
                            lineHeight: 1.15,
                          }}
                        >
                          {pos}
                        </Subheading>
                        {rows.map((p, idx) => (
                          <React.Fragment key={p.id}>
                            <BodyText style={{ fontSize: "0.95rem", fontWeight: 500 }}>{p.name}</BodyText>
                            <BodyText style={{ fontSize: "0.95rem", textAlign: "right" }}>
                              {typeof p.buyPrice === "number" ? `$${p.buyPrice.toFixed(1)}` : "-"}
                            </BodyText>
                            <BodyText style={{ fontSize: "0.95rem", textAlign: "right" }}>
                              {typeof p.currentPrice === "number" ? `$${p.currentPrice.toFixed(1)}` : "-"}
                            </BodyText>
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </div>
                {/* Totals row */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 60px 60px",
                    columnGap: 8,
                    alignItems: "center",
                    margin: "10px 0 6px 0",
                    fontSize: "0.97rem",
                  }}
                >
                  <BodyText variant="muted" style={{ fontWeight: 600, fontSize: "0.97rem" }}>
                    Total Team Value:
                  </BodyText>
                  <BodyText style={{ textAlign: "right", fontWeight: 600, fontSize: "0.95rem" }}>
                    {playersByPos && Object.values(playersByPos).flat().reduce((sum, p) => (typeof p.buyPrice === "number" ? sum + p.buyPrice : sum), 0) !== undefined
                      ? `$${Object.values(playersByPos).flat().reduce((sum, p) => (typeof p.buyPrice === "number" ? sum + p.buyPrice : sum), 0).toFixed(1)}`
                      : "-"}
                  </BodyText>
                  <BodyText style={{ textAlign: "right", fontWeight: 600, fontSize: "0.95rem" }}>
                  {
                    `$${Object.values(playersByPos)
                          .flat()
                          .reduce((sum, p) => 
                              typeof p.currentPrice === "number" ? sum + p.currentPrice : sum
                          , 0).toFixed(1)}`
                  }
                  </BodyText>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.95rem",
                    marginBottom: "6px",
                  }}
                >
                  <BodyText variant="muted" style={{ fontWeight: 600 }}>
                    Net Gain / Loss:
                  </BodyText>
                  <BodyText
                    style={{
                      fontWeight: 600,
                      color: netGainLoss >= 0 ? "#2e7d32" : "#c62828",
                    }}
                  >
                    {typeof netGainLoss === "number"
                      ? `${netGainLoss >= 0 ? "+" : ""}$${netGainLoss.toFixed(1)}`
                      : "-"}
                  </BodyText>
                </div>
                {/* Summary stats */}
                <div style={{ margin: "12px 0 8px 0" }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7, marginTop: 0 }}>
                  <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Budget Remaining:</BodyText>
                  <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{typeof budgetRemaining === "number" ? `$${budgetRemaining.toFixed(1)}` : "-"}</BodyText>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                  <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Transfers Used:</BodyText>
                  <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{transfersUsed}</BodyText>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                  <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Transfers Left:</BodyText>
                  <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{transfersAvailable}</BodyText>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                  <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Captain:</BodyText>
                  <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{nameOf(captain) || "-"}</BodyText>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                  <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Chip Used:</BodyText>
                  <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{chip ? chip : 'None'}</BodyText>
                </div>
                <Button
                  primary
                  onClick={() => navigate(`/pickteam/${leagueID}`)}
                  style={{ width: "100%", marginTop: 20 }}
                >
                  Pick Team
                </Button>
                <Button
                  onClick={() => navigate(`/yourpoints/${leagueID}`)}
                  style={{
                    width: "100%",
                    marginTop: 10,
                    backgroundColor: "#ff69b4",
                    color: "#fff",
                  }}
                >
                  Your Points
                </Button>
              </Panel>
            </div>
          )}
          {activeView === "pitch" && (
            <div style={{ marginTop: "0" }}>
              <div style={{ position: "relative" }}>
                <Pitch
                  team={selection}
                  playersById={new Map(Object.entries(enhancedPlayersById))}
                  positions={filteredPositions}
                  captainID={captain}
                  gwPrices={gwPrices}
                />
              </div>
              {/* Summary strip */}
              {hasSelectionThisGW && (() => {
                const pill = (label, extraStyle = {}) => (
                  <div key={label} style={{ background: "#f4f7f8", borderRadius: "999px", padding: "0.35rem 0.65rem", fontSize: "0.82rem", fontWeight: "600", color: "#234", whiteSpace: "nowrap", flex: "1 1 0", textAlign: "center", ...extraStyle }}>
                    {label}
                  </div>
                );
                const pills = [];
                if (typeof netGainLoss === "number" && totalBuyValue > 0) {
                  const gainColor = netGainLoss > 0 ? "#22c55e" : netGainLoss < 0 ? "#ef4444" : "#9ca3af";
                  const gainLabel = netGainLoss > 0 ? `+ Gain $${netGainLoss.toFixed(1)}m` : netGainLoss < 0 ? `- Loss $${Math.abs(netGainLoss).toFixed(1)}m` : `$0.0m`;
                  pills.push(pill(gainLabel, { color: gainColor }));
                }
                if (typeof budgetRemaining === "number") pills.push(pill(`$${budgetRemaining.toFixed(1)}m budget left`));
                if (transfersAvailable === 'unlimited') pills.push(pill('Unlimited FT'));
                else if (transfersAvailable !== '-') pills.push(pill(`${transfersAvailable} FT left`));
                return (
                  <div style={{ width: "100%", maxWidth: "420px", margin: "0.75rem auto 0", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "0.85rem 0.95rem", boxSizing: "border-box", boxShadow: "0 6px 18px rgba(0,0,0,0.06)" }}>
                    {/* Tier 1 — Hero value */}
                    <div style={{ textAlign: "center", marginBottom: "0.65rem" }}>
                      <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "42px", fontWeight: "800", lineHeight: 1, color: "#222", letterSpacing: "-1px" }}>
                        {teamValue > 0 ? `$${teamValue.toFixed(1)}m` : "—"}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "#9ca3af", marginTop: "3px", fontWeight: 500 }}>
                        {selectedGW} Value
                      </div>
                    </div>
                    {/* Tier 2 — Supporting pills, evenly spaced */}
                    {pills.length > 0 && (
                      <div style={{ display: "flex", gap: "0.45rem", justifyContent: "space-between" }}>
                        {pills}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </>
    );
  }

  // Desktop layout
  // Construct gwPrices object for Pitch
  const gwPrices = {};
  filteredPositions.forEach(pos => {
    (playersByPos[pos] || []).forEach(p => {
      gwPrices[p.id] = p.currentPrice;
    });
  });
  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", margin: "0.5rem 0 8px 0" }}>
        <GWSelector />
      </div>
      <div className="pitch-layout">
        {/* Left Panel */}
        <div className="pitch-left-panel">
          <Panel
            style={{
              width: "100%",
              maxWidth: 400,
              margin: "0 auto 1.2rem auto",
              borderRadius: "8px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              padding: "1.5rem 1.1rem"
            }}
          >
            {!hasSelectionThisGW && (
              <BodyText
                variant="muted"
                style={{ marginBottom: 10}}
              >
                No selections saved for {selectedGW}.
              </BodyText>
            )}
            {/* Player table grouped by position */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 60px", columnGap: 8, alignItems: "center" }}>
              {/* Header row */}
              <BodyText style={{ fontWeight: 600, fontSize: "1rem" }}>
                {(teamName || "Team")} • {(selectedGW || "—")}
              </BodyText>
              <BodyText style={{ textAlign: "right", fontWeight: 600 }}>Buy</BodyText>
              <BodyText style={{ textAlign: "right", fontWeight: 600 }}>{selectedGW}</BodyText>

              {/* Position groups */}
              {filteredPositions.map((pos) => {
                const rows = playersByPos[pos] || [];
                return (
                  <React.Fragment key={pos}>
                    <Subheading
                      variant="muted"
                      style={{
                        gridColumn: "1 / -1",
                        fontSize: "1rem",
                        fontWeight: 600,
                        margin: "10px 0 4px 0",
                        letterSpacing: 0.2,
                        lineHeight: 1.15,
                      }}
                    >
                      {pos}
                    </Subheading>

                    {rows.map((p, idx) => (
                      <React.Fragment key={p.id}>
                        <BodyText style={{ fontSize: "0.95rem", fontWeight: 500 }}>{p.name}</BodyText>
                        <BodyText style={{ fontSize: "0.95rem", textAlign: "right" }}>
                          {typeof p.buyPrice === "number" ? `$${p.buyPrice.toFixed(1)}` : "-"}
                        </BodyText>
                        <BodyText style={{ fontSize: "0.95rem", textAlign: "right" }}>
                          {typeof p.currentPrice === "number" ? `$${p.currentPrice.toFixed(1)}` : "-"}
                        </BodyText>
                      </React.Fragment>
                    ))}

                  </React.Fragment>
                );
              })}
            </div>

            {/* Totals row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 60px 60px",
                columnGap: 8,
                alignItems: "center",
                margin: "10px 0 6px 0",
                fontSize: "0.97rem",
              }}
            >
              <BodyText variant="muted" style={{ fontWeight: 600, fontSize: "0.97rem" }}>
                Total Team Value:
              </BodyText>
              <BodyText style={{ textAlign: "right", fontWeight: 600, fontSize: "0.95rem" }}>
                {playersByPos && Object.values(playersByPos).flat().reduce((sum, p) => (typeof p.buyPrice === "number" ? sum + p.buyPrice : sum), 0) !== undefined
                  ? `$${Object.values(playersByPos).flat().reduce((sum, p) => (typeof p.buyPrice === "number" ? sum + p.buyPrice : sum), 0).toFixed(1)}`
                  : "-"}
              </BodyText>
              <BodyText style={{ textAlign: "right", fontWeight: 600, fontSize: "0.95rem" }}>
                {`$${teamValue.toFixed(1)}`}
              </BodyText>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.95rem",
                marginBottom: "6px",
              }}
            >
              <BodyText variant="muted" style={{ fontWeight: 600 }}>
                Net Gain / Loss:
              </BodyText>
              <BodyText
                style={{
                  fontWeight: 600,
                  color: netGainLoss >= 0 ? "#2e7d32" : "#c62828",
                }}
              >
                {typeof netGainLoss === "number"
                  ? `${netGainLoss >= 0 ? "+" : ""}$${netGainLoss.toFixed(1)}`
                  : "-"}
              </BodyText>
            </div>
            <div
              style={{
                borderBottom: "1px solid #d1d5db",
                margin: "6px 0 14px 0",
              }}
            />

            {/* Summary stats */}
            <div style={{ margin: "12px 0 8px 0" }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7, marginTop: 0 }}>
              <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Budget Remaining:</BodyText>
              <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{typeof budgetRemaining === "number" ? `$${budgetRemaining.toFixed(1)}` : "-"}</BodyText>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
              <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Transfers Used:</BodyText>
              <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{transfersUsed}</BodyText>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
              <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Transfers Left:</BodyText>
              <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{transfersAvailable}</BodyText>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
              <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Captain:</BodyText>
              <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{nameOf(captain) || "-"}</BodyText>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
              <BodyText variant="muted" style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>Chip Used:</BodyText>
              <BodyText style={{ fontWeight: 400, fontSize: "0.95rem", lineHeight: 1.2 }}>{chip ? chip : 'None'}</BodyText>
            </div>

            {/* Navigation buttons */}
            <Button
              primary
              onClick={() => navigate(`/pickteam/${leagueID}`)}
              style={{ width: "100%", marginTop: 20 }}
            >
              Pick Team
            </Button>
            <Button
              onClick={() => navigate(`/yourpoints/${leagueID}`)}
              style={{
                width: "100%",
                marginTop: 10,
                backgroundColor: "#ff69b4",
                color: "#fff",
              }}
            >
              Your Points
            </Button>
          </Panel>
        </div>

        {/* Pitch */}
        <div className="pitch-field">
          <Pitch
            team={selection}
            playersById={new Map(Object.entries(enhancedPlayersById))}
            positions={filteredPositions}
            captainID={captain}
            gwPrices={gwPrices}
          />
        </div>
      </div>
    </>
  );
}